// /context/AuthContext.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api, { invalidateCsrfToken, prefetchCsrfToken } from "../../constants/api"; // baseURL = "/api"
import {
  clearAuthenticatedClientState,
  shouldClearCachesOnUserChange,
} from "../../src/utils/authClientCleanup.js";

const Ctx = createContext(null);
const LS_KEY = "auth.user.v1";
const REAUTH_COOLDOWN_MS = 10_000; // 10s – verhindert Spam bei schnellen Visibility-Wechseln

const norm = (r) =>
  (r || "")
    .toString()
    .trim()
    .toLowerCase()
    .replaceAll("ä", "ae")
    .replaceAll("ö", "oe")
    .replaceAll("ü", "ue");

function safeParse(json, fallback = null) {
  try {
    return JSON.parse(json);
  } catch {
    return fallback;
  }
}

function normalizeUser(u) {
  if (!u) return null;
  const out = { ...u };
  if (out.rolle) out.rolle = norm(out.rolle);
  if (out.rolle_id != null) out.rolle_id = Number(out.rolle_id);
  return out;
}

export function AuthProvider({ children }) {
  // 1) Sofort aus localStorage lesen, um FOUC zu vermeiden
  const cached = useMemo(
    () => normalizeUser(safeParse(localStorage.getItem(LS_KEY), null)),
    []
  );
  const [user, setUser] = useState(cached);
  const [features, setFeatures] = useState([]);
  const [needAcademy, setNeedAcademy] = useState(false);
  const [sharepointReady, setSharepointReady] = useState(false);
  const [loadingAuth, setLoadingAuth] = useState(cached == null); // wenn Cache da, starten wir "fertig"
  const lastAuthRef = useRef(0);
  const abortRef = useRef(null);
  const userRef = useRef(cached);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const setUserBoth = useCallback((u, { skipCacheClear = false } = {}) => {
    const nu = normalizeUser(u);
    const prev = userRef.current;
    if (!skipCacheClear && shouldClearCachesOnUserChange(prev, nu)) {
      clearAuthenticatedClientState();
    }
    setUser(nu);
    userRef.current = nu;
    if (!nu) {
      setFeatures([]);
      setNeedAcademy(false);
      setSharepointReady(false);
    }
    try {
      if (nu) localStorage.setItem(LS_KEY, JSON.stringify(nu));
      else localStorage.removeItem(LS_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const refreshAuth = useCallback(async (force = false) => {
    // Cooldown gegen exzessive Re-Auth
    const now = Date.now();
    if (!force && now - lastAuthRef.current < REAUTH_COOLDOWN_MS) return;
    lastAuthRef.current = now;

    // Bereits laufende Anfrage abbrechen
    try {
      abortRef.current?.abort?.();
    } catch {
      /* ignore */
    }

    const controller = new AbortController();
    abortRef.current = controller;

    // Wenn kein Cache vorhanden ist, zeige Loader; sonst silent refresh.
    setLoadingAuth((prev) => (user == null ? true : prev));
    try {
      const res = await api.get("/check-login", { signal: controller.signal });
      const u = res?.data?.user || null;
      const feat = Array.isArray(res?.data?.features) ? res.data.features : [];
      setFeatures(feat);
      setNeedAcademy(!!res?.data?.needAcademy);
      setSharepointReady(!!res?.data?.sharepointReady);
      setUserBoth(u);
      if (u) {
        try {
          await prefetchCsrfToken();
        } catch {
          /* optional – POST-Interceptor holt Token bei Bedarf nach */
        }
      }
    } catch {
      setUserBoth(null);
      setFeatures([]);
      setNeedAcademy(false);
    } finally {
      setLoadingAuth(false);
    }
  }, [setUserBoth, user]);

  // 2) Initiale Abfrage (falls kein Cache) oder sanfter Refresh (falls Cache)
  useEffect(() => {
    let alive = true;
    (async () => {
      await refreshAuth();
      if (!alive) return;
    })();
    return () => {
      alive = false;
      try {
        abortRef.current?.abort?.();
      } catch {
        /* ignore */
      }
    };
  }, [refreshAuth]);

  // 3) Cross-Tab-Sync (wenn in anderem Tab eingeloggt/ausgeloggt)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === LS_KEY) {
        const next = normalizeUser(safeParse(e.newValue, null));
        const prev = userRef.current;
        if (shouldClearCachesOnUserChange(prev, next)) {
          clearAuthenticatedClientState();
        }
        setUser(next);
        userRef.current = next;
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // 4) Re-Auth bei Tab-Rückkehr (sichtbar), z. B. nach Session-Timeout
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") {
        refreshAuth();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [refreshAuth]);

  // 5) Role-Helper
  const hasRole = useMemo(() => {
    return (roles) =>
      !!user?.rolle && Array.isArray(roles) && roles.map(norm).includes(user.rolle);
  }, [user]);

  const hasAnyRole = useMemo(() => {
    return (roles) => {
      if (!user?.rolle || !Array.isArray(roles)) return false;
      const set = new Set(roles.map(norm));
      return set.has(user.rolle);
    };
  }, [user]);

  const roleIdIn = useMemo(() => {
    return (ids) => {
      if (!Array.isArray(ids)) return false;
      const n = Number(user?.rolle_id);
      return Number.isFinite(n) && ids.some((x) => Number(x) === n);
    };
  }, [user]);

  const featureSet = useMemo(() => new Set(features), [features]);

  const hasFeature = useCallback(
    (key) => {
      const k = String(key || "").trim();
      return k.length > 0 && featureSet.has(k);
    },
    [featureSet]
  );

  // Canonical authenticated client logout + cache boundary cleanup
  const signOut = useCallback(async () => {
    try {
      await api.post("/logout").catch(() => {});
    } finally {
      invalidateCsrfToken();
      clearAuthenticatedClientState();
      try {
        sessionStorage.clear();
      } catch {
        /* ignore */
      }
      // skipCacheClear: already cleared above; still remove auth.user.v1
      setUserBoth(null, { skipCacheClear: true });
      setFeatures([]);
      setNeedAcademy(false);
      setSharepointReady(false);
    }
  }, [setUserBoth]);

  const value = useMemo(
    () => ({
      user,
      features,
      needAcademy,
      sharepointReady,
      loadingAuth,
      hasRole,
      hasAnyRole,
      roleIdIn,
      hasFeature,
      refreshAuth,
      signOut,
    }),
    [
      user,
      features,
      needAcademy,
      sharepointReady,
      loadingAuth,
      hasRole,
      hasAnyRole,
      roleIdIn,
      hasFeature,
      refreshAuth,
      signOut,
    ]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

/** Optional Auth — z. B. FilePreview ohne Provider (Tests / isolierte Mounts). */
export function useAuthOptional() {
  return useContext(Ctx);
}
